import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { HardDrive } from 'lucide-react'

import { keys } from '@/api/keys'
import { cacheStatsQuery, sweepCache } from '@/api/settings'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { formatBytes } from '@/lib/format'

/**
 * How much disk the derived-media cache is using (§5.5
 * `/api/media/cache/stats`), and the button that sweeps the oldest entries.
 *
 * Pinned bytes (the `char_thumb` set every character editor needs) are shown
 * separately because a sweep never touches them — otherwise the number
 * looks like it refuses to go down.
 */
export function CacheStatsCard() {
  const qc = useQueryClient()
  const query = useQuery(cacheStatsQuery)

  const sweep = useMutation({
    mutationFn: sweepCache,
    onSuccess: (next) => {
      qc.setQueryData(keys.media.cacheStats(), next)
      toast.success('Cache tidied', { description: `${formatBytes(next.bytes)} in use now.` })
    },
    onError: (e) => toast.error('Could not tidy the cache', { description: e instanceof Error ? e.message : String(e) }),
  })

  const s = query.data
  const pct = s && s.max_bytes > 0 ? Math.min(1, s.bytes / s.max_bytes) : 0
  const tight = s ? s.free_bytes < 2 * 1024 ** 3 : false

  return (
    <section className="card p-5" aria-labelledby="cache-heading">
      <header className="flex items-center justify-between gap-3">
        <h2 id="cache-heading" className="flex items-center gap-2 text-lg font-semibold text-ink">
          <HardDrive className="size-5 text-ink-2" aria-hidden />
          Prepared pictures
        </h2>
        <Button size="sm" variant="ghost" onClick={() => sweep.mutate()} loading={sweep.isPending}>
          Tidy up
        </Button>
      </header>

      {query.isPending ? (
        <p className="mt-3 text-sm text-ink-2">Measuring…</p>
      ) : query.isError || !s ? (
        <p className="mt-3 text-sm text-ink-2">The cache could not be measured right now.</p>
      ) : (
        <>
          <p className="mt-3 text-sm text-ink-2">
            Thumbnails, sprite sheets and posters the studio has already made, so a screen opens instantly the second time.
          </p>

          <div className="mt-4 h-2 overflow-hidden rounded-full bg-paper-3" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)} aria-label="Cache used">
            <div className={cn('h-full rounded-full transition-[width]', pct > 0.9 ? 'bg-warn' : 'bg-accent')} style={{ width: `${Math.round(pct * 100)}%` }} />
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Row label="In use" value={formatBytes(s.bytes)} />
            <Row label="Allowed" value={formatBytes(s.max_bytes)} />
            <Row label="Kept always" value={formatBytes(s.pinned_bytes)} />
            <Row label="Free on disk" value={formatBytes(s.free_bytes)} warn={tight} />
          </dl>
          <p className="mt-2 text-xs text-ink-3">{s.entries.toLocaleString()} files.</p>

          {tight ? (
            <p className="mt-3 rounded-xl border border-warn bg-warn-soft px-3 py-2 text-sm text-ink" role="status">
              Disk is getting tight. Tidying up frees the least-used pictures; the ones characters need are kept.
            </p>
          ) : null}
        </>
      )}
    </section>
  )
}

function Row({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className={warn ? 'font-medium text-warn tabular-nums' : 'font-medium text-ink tabular-nums'}>{value}</dd>
    </div>
  )
}
